// Ending a session when the server says it is over.
//
// A token can stop being accepted without the interface noticing: it expires, the
// signing secret is rotated, or the account is removed. Every request then comes
// back 401, and the user was left staring at whatever failed rather than being
// returned to the login page. These helpers are the one place that ends a session,
// so the interface, the GraphQL client and the file helpers all behave the same.

const LOGIN_PATH = '/login';

// react-admin uses hash routing, so the login page is normally "#/login"; the
// pathname is checked as well in case that ever changes.
function isOnLoginPage() {
    if (typeof window === 'undefined') return false;

    const {hash, pathname} = window.location;
    return hash.startsWith(`#${LOGIN_PATH}`) || pathname === LOGIN_PATH;
}

// Clears everything a signed-in session leaves behind.
//
// 'roles' has to go with the token. getPermissions reads that key, so leaving it
// behind leaves the previous user's permissions in place and the interface gates
// on them.
export function clearSession() {
    if (typeof window === 'undefined') return;

    localStorage.removeItem('token');
    localStorage.removeItem('roles');
}

// Several requests usually fail together when a session ends, so this is
// deliberately idempotent: the first caller ends the session, the rest are no-ops
// rather than a pile of competing redirects.
let endingSession = false;

export function forceLogout() {
    clearSession();

    if (typeof window === 'undefined') return;
    if (endingSession || isOnLoginPage()) return;

    endingSession = true;

    // A reload rather than a route change. The Apollo cache and react-admin's
    // in-memory permissions both still hold the ended session, and navigating
    // would carry them into the next page. With the token gone, react-admin finds
    // no session on the way back up and shows the login page.
    window.location.reload();
}

// Marks a session as over when a response says the caller is not authenticated.
// Returns true when it was, so a caller can skip its own error handling.
export function noteAuthFailure(response) {
    if (response && response.status === 401) {
        forceLogout();
        return true;
    }

    return false;
}